import {
  CHECKOUT_MODAL_PATH,
  ERROR_PATH,
  PROVIDER_BASE_PATH,
  PROVIDER_PATHS,
  RECEIPT_MODAL_PATH,
  REDIRECT_PATH,
  ROOT_PATH,
} from 'shared/consts';
import * as Sentry from '@sentry/react';
import { Route, Switch } from 'react-router-dom';
import { withBuildingPrefix } from 'utils/pathHelpers';
import React, { Suspense } from 'react';
import { useIntl } from 'react-intl';

import { AppContentWrapper } from 'components/app-content-wrapper/app-content-wrapper';
import { DefaultLoader } from 'components/default-loader/default-loader';
import { DefaultRedirect } from './default-redirect';
import { ErrorNotification } from '@hqo/react-components-library/dist/molecules/errorNotification';
import { PrivateRoute } from 'components/private-route';
import { lazyWithRetry } from 'utils/lazyWithRetry';

// Lazy load pages for code splitting (with stale-chunk retry/reload)
const AvailabilityCalendarPage = lazyWithRetry(() =>
  import('pages/availability-calendar').then(module => ({ default: module.AvailabilityCalendarPage })),
);
const CheckoutModal = lazyWithRetry(() =>
  import('components/modals/checkout-modal').then(module => ({ default: module.CheckoutModal })),
);
const ClassDetails = lazyWithRetry(() =>
  import('pages/class-details').then(module => ({ default: module.ClassDetails })),
);
const ErrorPage = lazyWithRetry(() => import('pages/error').then(module => ({ default: module.ErrorPage })));
const EventDetails = lazyWithRetry(() =>
  import('pages/event-details').then(module => ({ default: module.EventDetails })),
);
const EventTimeSlotDetails = lazyWithRetry(() =>
  import('pages/event-time-slot-details').then(module => ({ default: module.EventTimeSlotDetails })),
);
const EventTimeSlots = lazyWithRetry(() =>
  import('pages/event-time-slots').then(module => ({ default: module.EventTimeSlots })),
);
const GymCardPage = lazyWithRetry(() => import('pages/gym-card').then(module => ({ default: module.GymCardPage })));
const ReceiptModal = lazyWithRetry(() =>
  import('components/modals/receipt-modal').then(module => ({ default: module.ReceiptModal })),
);
const RedirectPage = lazyWithRetry(() =>
  import('components/payment/redirect').then(module => ({ default: module.RedirectPage })),
);
const SchedulePage = lazyWithRetry(() => import('pages/schedule').then(module => ({ default: module.SchedulePage })));
const ServiceProviderPage = lazyWithRetry(() =>
  import('pages/service-provider').then(module => ({ default: module.ServiceProviderPage })),
);
const ServiceProvidersPage = lazyWithRetry(() =>
  import('pages/service-providers').then(module => ({ default: module.ServiceProvidersPage })),
);
const ServiceTypePage = lazyWithRetry(() =>
  import('pages/service-type').then(module => ({ default: module.ServiceTypePage })),
);

const SentryRoute = Sentry.withSentryRouting(Route);

const handleReload = () => window.location.reload();

const RouterErrorFallback = () => {
  const intl = useIntl();

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
      <ErrorNotification
        title={intl.formatMessage({ id: 'something_went_wrong' })}
        description={intl.formatMessage({ id: 'unexpected_error_occurred' })}
        buttonText={intl.formatMessage({ id: 'go_back' })}
        onClick={handleReload}
      />
    </div>
  );
};

export const Router: React.FC = (): JSX.Element => (
  <AppContentWrapper>
    <Sentry.ErrorBoundary fallback={<RouterErrorFallback />}>
      <Suspense fallback={<DefaultLoader />}>
        <Switch>
          {/* Routes that support both /providers and /building/:buildingUuid/providers patterns */}
          <PrivateRoute component={ClassDetails} path={withBuildingPrefix(PROVIDER_PATHS.CLASS_DETAILS)} />
          <PrivateRoute
            component={EventTimeSlotDetails}
            path={withBuildingPrefix(PROVIDER_PATHS.EVENT_TIME_SLOTS_DETAILS)}
          />
          <PrivateRoute component={EventTimeSlots} path={withBuildingPrefix(PROVIDER_PATHS.EVENT_TIME_SLOTS)} />
          <PrivateRoute component={EventDetails} path={withBuildingPrefix(PROVIDER_PATHS.EVENT_DETAILS)} />
          <PrivateRoute component={GymCardPage} exact path={withBuildingPrefix(PROVIDER_PATHS.GYM_CARD)} />
          <PrivateRoute component={ReceiptModal} exact path={withBuildingPrefix(RECEIPT_MODAL_PATH)} />
          <PrivateRoute component={CheckoutModal} exact path={withBuildingPrefix(CHECKOUT_MODAL_PATH)} />
          <PrivateRoute component={ServiceTypePage} path={withBuildingPrefix(PROVIDER_PATHS.SERVICE_TYPE)} />
          <PrivateRoute component={AvailabilityCalendarPage} path={withBuildingPrefix(PROVIDER_PATHS.AVAILABILITY)} />
          <PrivateRoute component={SchedulePage} path={withBuildingPrefix(PROVIDER_PATHS.SCHEDULE)} />
          {/* the /:paymentPath? allows the payments modal to display on the provider details page */}
          <PrivateRoute
            component={ServiceProviderPage}
            path={withBuildingPrefix(`${PROVIDER_BASE_PATH}/:paymentPath?`)}
          />
          <PrivateRoute component={ServiceProvidersPage} path={withBuildingPrefix(ROOT_PATH)} />
          <PrivateRoute component={RedirectPage} path={REDIRECT_PATH} />
          <SentryRoute component={ErrorPage} path={ERROR_PATH} />
          <SentryRoute component={DefaultRedirect} />
        </Switch>
      </Suspense>
    </Sentry.ErrorBoundary>
  </AppContentWrapper>
);
